import { Component, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '../../services/toast.service';
import { DialogService } from '../../services/dialog.service';
import { AccountService } from '../../api/services/account.service';
import {
  AccountSelectionRefreshResult,
  AccountStateService,
} from '../../services/account-state.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  standalone: false,
})
export class LoginComponent {
  twoFactor = false;

  /**
   * Form used to log in for existing users
   */
  loginForm = new FormGroup({
    email: new FormControl('', [Validators.email, Validators.required]),
    password: new FormControl('', Validators.required),
    twoFactorCode: new FormControl<string>(''),
  });

  /**
   * Form used to start the registration process
   */
  registerForm = new FormGroup({
    email: new FormControl('', [Validators.email, Validators.required]),
    firstName: new FormControl('', Validators.required),
  });
  readonly loading = signal<boolean>(false);

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
    private dialogService: DialogService,
    private accountService: AccountService,
    private toastService: ToastService,
    private accountStateService: AccountStateService,
  ) {}

  async login() {
    const twoFactorAuthenticationCode = this.loginForm.value.twoFactorCode
      ? this.loginForm.value.twoFactorCode
      : undefined;

    this.loading.set(true);

    this.authService
      .login(
        this.loginForm.value.email!,
        this.loginForm.value.password!,
        twoFactorAuthenticationCode,
      )
      .subscribe({
        next: async (res) => {
          const tokens = res.body;
          this.authService.setTokens(tokens);

          let accountSelection: AccountSelectionRefreshResult;
          try {
            accountSelection =
              await this.accountStateService.refreshAccountsAndReconcileSelected(
                tokens.selectedAccountGuid,
              );
          } catch (error) {
            console.error(error);
            this.loading.set(false);
            this.authService.clearAuthState();
            this.toast.error('Could not load your accounts');
            return;
          }

          this.loading.set(false);

          if (accountSelection.status === 'no-active-accounts') {
            this.authService.clearAuthState();
            this.toast.error(
              'Your account is inactive. Please contact support or your account administrator.',
            );
            return;
          }

          if (tokens.shouldPromptTwoFactorSetup) {
            // Go to 2FA setup page
            void this.router.navigate(['admin/setup-two-factor'], {
              state: {
                shouldPromptEmailVerification:
                  tokens.shouldPromptEmailVerification,
                userGuid: tokens.userGuid,
              },
            });
          } else if (tokens.shouldPromptEmailVerification) {
            // Go to email verification setup page
            void this.router.navigate(['admin/setup-email-verification'], {
              state: { userGuid: tokens.userGuid },
            });
          } else {
            // Redirect to returnUrl if set in query, otherwise go to home page
            const redirectUrl =
              this.route.snapshot.queryParams['returnUrl'] || 'admin/home';
            void this.router.navigateByUrl(redirectUrl);
          }
        },
        error: (err) => {
          this.loading.set(false);
          if (err.status === 422) {
            // If we get 422 (unprocessable entity) response, we also need a 2FA token
            this.twoFactor = true;
            this.toast.info('2FA Authentication Code Required');
          } else {
            // Otherwise, reset the form as the entered credentials are incorrect
            this.twoFactor = false;
            this.loginForm.reset();
            this.toast.error('Login Credentials Invalid');
          }
        },
      });
  }

  register() {
    // Validate email address first
    this.accountService
      .emailAddressAvailablePost$Response(
        { emailAddress: this.registerForm.controls.email.value! },
        this.authService.getAuthorisationHeaders(),
      )
      .subscribe({
        next: () => {
          // Route to registration page, passing email and name
          void this.router.navigate(['admin/register'], {
            state: {
              firstName: this.registerForm.controls.firstName.value,
              email: this.registerForm.controls.email.value,
            },
          });
        },
        error: () => this.toastService.error('Email address already in use'),
      });
  }

  forgotPassword() {
    void this.router.navigate(['admin', 'forgot-password']);
  }
}
